import React, { useMemo } from 'react';
import { Badge, Button, LoadingSpinner } from '@tracekit/core';
import { Rss } from 'lucide-react';
import { RSSFeedItem } from '../services/usGovernmentIntegrations';
import { RSSFeedSource } from '../services/rssFeedRegistry';
import { stripHtml } from '../utils/htmlToPlainText';
import {
  formatPublishedAt,
  formatPublishedAtWithRelative,
  getFeedHomepageUrl,
  getFeedSourceDisplayName,
  resolveRssFeedSource,
} from '../utils/industryNewsFormatting';
import { splitTextBySearchHighlight } from '../utils/industryNewsSearch';
import { MatchScoreTooltip } from './government-data/MatchScoreTooltip';

interface IndustryNewsArticleCardProps {
  item: RSSFeedItem;
  /** Opens the in-app article modal (card body click). */
  onView: (item: RSSFeedItem) => void;
  showExtendedBadges?: boolean;
  relevanceScore?: number;
  relevanceScorePending?: boolean;
  highlightQuery?: string;
  titleClassName?: string;
  containerClassName?: string;
}

function HighlightedText({
  text,
  query,
  className,
}: {
  text: string;
  query?: string;
  className?: string;
}) {
  const segments = useMemo(
    () => splitTextBySearchHighlight(text, query ?? ''),
    [text, query]
  );

  if (!query?.trim()) {
    return <span className={className}>{text}</span>;
  }

  return (
    <span className={className}>
      {segments.map((segment, index) =>
        segment.highlight ? (
          <mark
            key={`${index}-${segment.text}`}
            className="rounded bg-yellow-200 px-0.5 text-inherit dark:bg-yellow-800"
          >
            {segment.text}
          </mark>
        ) : (
          <span key={`${index}-${segment.text}`}>{segment.text}</span>
        )
      )}
    </span>
  );
}

function FeedTypeBadge({ feedSource }: { feedSource: RSSFeedSource }) {
  if (!feedSource.feedType) {
    return null;
  }

  const isOpportunity = feedSource.feedType === 'opportunities';

  return (
    <Badge
      variant="primary"
      className={`text-xs ${
        isOpportunity
          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
          : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      }`}
    >
      {isOpportunity ? '📋 Opportunity' : '📰 News'}
    </Badge>
  );
}

export function IndustryNewsArticleCard({
  item,
  onView,
  showExtendedBadges = false,
  relevanceScore,
  relevanceScorePending = false,
  highlightQuery,
  titleClassName = 'font-semibold text-gray-900 dark:text-gray-100 mb-1',
  containerClassName = 'p-3 bg-gray-50 dark:bg-slate-800 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors',
}: IndustryNewsArticleCardProps) {
  const feedSource = resolveRssFeedSource(item);
  const sourceName = getFeedSourceDisplayName(item, feedSource);
  const homepageUrl = feedSource ? getFeedHomepageUrl(feedSource) : null;
  const plainDescription = stripHtml(item.description);

  const openExternalArticle = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (item.link) {
      window.open(item.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={`${containerClassName} cursor-pointer`}
      role="button"
      tabIndex={0}
      onClick={() => onView(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onView(item);
        }
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0 pr-3">
          <h4 className={titleClassName}>
            <HighlightedText text={item.title} query={highlightQuery} />
          </h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
            <HighlightedText text={plainDescription} query={highlightQuery} />
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {relevanceScorePending ? (
              <span
                className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-1 text-xs font-medium text-gray-500 dark:border-slate-600 dark:bg-slate-900 dark:text-gray-400"
                aria-label="Ranking relevance score"
              >
                <LoadingSpinner size="sm" />
                Ranking…
              </span>
            ) : (
              relevanceScore !== undefined && (
                <MatchScoreTooltip score={relevanceScore} variant="badge" />
              )
            )}
            {homepageUrl ? (
              <a
                href={homepageUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={`Visit ${sourceName}`}
                className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-50 hover:underline dark:text-blue-300 dark:hover:bg-blue-900/30"
                onClick={(event) => event.stopPropagation()}
              >
                <Rss className="h-3 w-3" />
                {sourceName}
              </a>
            ) : (
              <Badge variant="primary" className="flex items-center gap-1 text-xs">
                <Rss className="h-3 w-3" />
                {sourceName}
              </Badge>
            )}
            {showExtendedBadges && feedSource && <FeedTypeBadge feedSource={feedSource} />}
            {showExtendedBadges && feedSource?.priority === 1 && (
              <Badge variant="primary" className="text-xs text-green-600">
                High Priority
              </Badge>
            )}
            <span
              className="text-xs text-gray-500 dark:text-gray-400"
              title={formatPublishedAt(item.pubDate)}
            >
              {formatPublishedAtWithRelative(item.pubDate)}
            </span>
          </div>
        </div>
        <Button
          variant="primary"
          size="sm"
          className="shrink-0"
          onClick={openExternalArticle}
        >
          View
        </Button>
      </div>
    </div>
  );
}
